import { useMemo } from 'react';
import {
  Lookup as ILookup,
  useMetadata,
} from '../../../../contexts/MetadataContext';
import { Lookup } from '../../../../system/lookup';
import { PropertyType as Type } from '../../../../system/property-type';
import { PropertyField } from '../../../../system/field';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../../../flags';

import { ListingResource, useListingField } from '@brightmls/nextgen.lae.flc';

export const usePropertyType = (highlight: string[]) => {
  const { lookup, displayValueForLookup } = useMetadata();
  const { hasFeature, getFeatureValue } = useFeatureFlags();

  const propertyType = useListingField<string>(
    ListingResource.Property,
    PropertyField.PROPERTY_TYPE,
    '',
  );

  const propertyOptions = useMemo(() => {
    let options: ILookup[] = (lookup(Lookup.PROPERTY_TYPE) as ILookup[]) || [];

    if (hasFeature(FeatureFlags.CREATE_PROPERTY_TYPE_FILTER)) {
      const filter = getFeatureValue(FeatureFlags.CREATE_PROPERTY_TYPE_FILTER);
      options = options.filter((o) => filter.includes(o.LookupValue));
    }

    return options.map((lookup) => {
      return {
        ...lookup,
        LookupKey: lookup.LookupKey || lookup.LookupValue,
      };
    });
  }, [lookup, hasFeature, getFeatureValue]);

  const highlightedOptions = useMemo(
    () =>
      propertyOptions.filter((option) =>
        highlight.includes(displayValueForLookup(option)),
      ),
    [propertyOptions, highlight],
  );

  const nonHighlightedOptions = useMemo(
    () =>
      propertyOptions.filter(
        (option) => !highlight.includes(displayValueForLookup(option)),
      ),
    [propertyOptions, highlight],
  );

  const handleChange = (lookupKey: string) => {
    const propertyValue = propertyOptions.find(
      (o) => o.LookupKey === lookupKey,
    );
    if (propertyValue) {
      propertyType.updateValue(propertyValue.LookupValue);
    }
  };

  return {
    value: propertyType.value,
    propertyOptions,
    highlightedOptions,
    nonHighlightedOptions,
    handleChange,
  };
};
